import { ReaderChapter } from '@/services/booksApi';
import { resolveAssetUrl } from '@/utils/offlineAssets';

const EXECUTABLE_SCHEMES = new Set(['javascript:', 'vbscript:']);

const resolveUrl = (value: string, baseUrl: string): URL =>
  new URL(value, new URL(baseUrl, document.baseURI));

export const clamp = (minimum: number, value: number, maximum: number): number =>
  Math.max(minimum, Math.min(value, maximum));

export const estimateChapterHeight = (chapter: ReaderChapter): number =>
  clamp(320, 160 + chapter.blockCount * 48 + chapter.imageCount * 240, 6000);

const resolveUrlValue = (value: string, baseUrl: string, isLink: boolean): string => {
  const trimmed = value.trim();
  if (!trimmed || trimmed.startsWith('#')) {
    return value;
  }
  try {
    const url = resolveUrl(trimmed, baseUrl);
    if (EXECUTABLE_SCHEMES.has(url.protocol)) {
      return isLink ? '#' : '';
    }
    const absolute = url.toString();
    // Media gets redirected to a local copy when there is one; links never do.
    // resolveReaderLink() routes in-book navigation by matching an anchor's
    // href against chapter htmlUrls, and a local file URL would match nothing.
    return isLink ? absolute : resolveAssetUrl(absolute);
  } catch {
    return value;
  }
};

export const rewriteSrcSet = (value: string, baseUrl: string): string => {
  const candidates: string[] = [];
  let position = 0;
  while (position < value.length) {
    while (position < value.length && (value[position] === ',' || /\s/.test(value[position]))) position += 1;
    if (position >= value.length) break;
    const urlStart = position;
    const isDataUrl = value.slice(position, position + 5).toLowerCase() === 'data:';
    while (
      position < value.length &&
      !/\s/.test(value[position]) &&
      (isDataUrl || value[position] !== ',')
    ) position += 1;
    const url = value.slice(urlStart, position);
    while (position < value.length && /\s/.test(value[position])) position += 1;
    const descriptorStart = position;
    while (position < value.length && value[position] !== ',') position += 1;
    const descriptor = value.slice(descriptorStart, position).trim();
    if (position < value.length && value[position] === ',') position += 1;
    candidates.push(`${resolveUrlValue(url, baseUrl, false)}${descriptor ? ` ${descriptor}` : ''}`);
  }
  return candidates.join(', ');
};

export const rewriteFragmentUrls = (root: HTMLElement, baseUrl: string): void => {
  const elements = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];
  for (const element of elements) {
    if (element.hasAttribute('src')) {
      element.setAttribute('src', resolveUrlValue(element.getAttribute('src') ?? '', baseUrl, false));
    }
    if (element.hasAttribute('poster')) {
      element.setAttribute('poster', resolveUrlValue(element.getAttribute('poster') ?? '', baseUrl, false));
    }
    if (element.hasAttribute('data') && element.tagName === 'OBJECT') {
      element.setAttribute('data', resolveUrlValue(element.getAttribute('data') ?? '', baseUrl, false));
    }
    if (element.hasAttribute('srcset')) {
      element.setAttribute('srcset', rewriteSrcSet(element.getAttribute('srcset') ?? '', baseUrl));
    }
    if (element.hasAttribute('href')) {
      element.setAttribute('href', resolveUrlValue(element.getAttribute('href') ?? '', baseUrl, true));
    }
  }
};

export const parseChapterFragment = (
  rawHtml: string,
  chapter: Pick<ReaderChapter, 'id' | 'htmlUrl'>,
): HTMLElement => {
  const template = document.createElement('template');
  template.innerHTML = rawHtml;
  const meaningfulNodes = Array.from(template.content.childNodes).filter((node) => {
    if (node.nodeType === Node.COMMENT_NODE) {
      return false;
    }
    return node.nodeType !== Node.TEXT_NODE || Boolean(node.textContent?.trim());
  });
  if (meaningfulNodes.length !== 1 || !(meaningfulNodes[0] instanceof HTMLElement)) {
    throw new Error(`Chapter '${chapter.id}' must contain exactly one root element`);
  }

  const root = meaningfulNodes[0];
  const forbiddenSelector = 'html,head,body,script,style,link[rel~="stylesheet"]';
  if (root.matches(forbiddenSelector) || root.querySelector(forbiddenSelector)) {
    throw new Error(`Chapter '${chapter.id}' contains a forbidden document or executable element`);
  }
  if (root.dataset.chapterId !== chapter.id) {
    throw new Error(`Chapter fragment identity does not match '${chapter.id}'`);
  }

  rewriteFragmentUrls(root, chapter.htmlUrl);
  return root;
};

export interface ReaderLinkTarget {
  chapterId: string;
  fragmentId: string | null;
}

const normalizeWithoutHash = (value: string, baseUrl: string): string | null => {
  try {
    const url = resolveUrl(value, baseUrl);
    url.hash = '';
    return url.toString();
  } catch {
    return null;
  }
};

export const resolveReaderLink = (
  anchor: HTMLAnchorElement,
  ownerChapter: ReaderChapter,
  chapters: ReaderChapter[],
): ReaderLinkTarget | null => {
  const explicitChapterId = anchor.dataset.chapterId;
  const explicitPath = anchor.dataset.chapterPath;
  const explicitFragment = anchor.dataset.fragmentId;
  const rawHref = anchor.getAttribute('href') ?? '';
  const hrefHash = (() => {
    try {
      return resolveUrl(rawHref || '#', ownerChapter.htmlUrl).hash.slice(1) || null;
    } catch {
      return rawHref.startsWith('#') ? rawHref.slice(1) || null : null;
    }
  })();

  if (explicitChapterId) {
    const chapter = chapters.find((candidate) => candidate.id === explicitChapterId);
    return chapter ? { chapterId: chapter.id, fragmentId: explicitFragment ?? hrefHash } : null;
  }

  if (explicitPath) {
    const chapter = chapters.find((candidate) =>
      candidate.basename === explicitPath ||
      resolveUrl(candidate.htmlUrl, ownerChapter.htmlUrl).pathname.endsWith(explicitPath)
    );
    return chapter ? { chapterId: chapter.id, fragmentId: explicitFragment ?? hrefHash } : null;
  }

  if (rawHref.startsWith('#')) {
    return { chapterId: ownerChapter.id, fragmentId: rawHref.slice(1) || null };
  }

  const targetUrl = normalizeWithoutHash(rawHref, ownerChapter.htmlUrl);
  if (!targetUrl) {
    return null;
  }
  const chapter = chapters.find(
    (candidate) => normalizeWithoutHash(candidate.htmlUrl, ownerChapter.htmlUrl) === targetUrl
  );
  return chapter ? { chapterId: chapter.id, fragmentId: explicitFragment ?? hrefHash } : null;
};

export class LruCache<Key, Value> {
  private readonly entries = new Map<Key, Value>();

  constructor(private readonly limit: number) {}

  get(key: Key): Value | undefined {
    const value = this.entries.get(key);
    if (value !== undefined) {
      this.entries.delete(key);
      this.entries.set(key, value);
    }
    return value;
  }

  set(key: Key, value: Value): void {
    this.entries.delete(key);
    this.entries.set(key, value);
    while (this.entries.size > this.limit) {
      const oldest = this.entries.keys().next().value as Key | undefined;
      if (oldest === undefined) {
        break;
      }
      this.entries.delete(oldest);
    }
  }

  delete(key: Key): void {
    this.entries.delete(key);
  }

  clear(): void {
    this.entries.clear();
  }

  get size(): number {
    return this.entries.size;
  }
}
